
import { Component, ChangeDetectionStrategy, signal, computed, inject, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, effect, AfterViewInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Customer, Prescription, StorePartner } from '../../models/index';
import { AuthService } from '../../services/auth.service';
import { PosStateService } from '../../services/pos-state.service';
import { DataService } from '../../services/data.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { VideoCallModalComponent } from '../video-call-modal/video-call-modal.component';
import { VideoCallService } from '../../services/video-call.service';
import { GeminiService } from '../../services/gemini.service';
import { SupabaseClient } from '@supabase/supabase-js';
import { getSupabase } from '../../firebase';
import { TranslationService } from '../../services/translation.service';
import { NotificationService } from '../../services/notification.service';
import { PrivacyPolicyModalComponent } from '../privacy-policy-modal/privacy-policy-modal.component';
import { CustomerService } from '../../services/customer.service';
import { PrescriptionFormComponent } from '../prescription-form/prescription-form.component';
import { InputMaskDirective } from '../../directives/input-mask.directive';
import { BillingService } from '../../services/billing.service'; // Added BillingService

declare var html2canvas: any;

type IntakeStep = 'welcome' | 'kyc' | 'kyc-review' | 'id-card-photo' | 'photo' | 'symptoms' | 'waiting' | 'completed' | 'rejected';
type InputMethod = 'card' | 'ocr' | 'upload' | 'manual' | null;
type UploadTarget = 'ocr' | 'idcard'; 

@Component({
  selector: 'app-pos',
  standalone: true,
  templateUrl: './pos.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    RouterLink,
    VideoCallModalComponent,
    PrivacyPolicyModalComponent,
    PrescriptionFormComponent,
    InputMaskDirective
  ],
  providers: [VideoCallService, GeminiService, CustomerService]
})
export class PosComponent implements OnInit, OnDestroy, AfterViewInit {
  public authService = inject(AuthService); 
  private dataService = inject(DataService); 
  private customerService = inject(CustomerService);
  public posState = inject(PosStateService);
  private videoCallService = inject(VideoCallService);
  private geminiService = inject(GeminiService);
  private translationService = inject(TranslationService);
  public notificationService = inject(NotificationService);
  private billingService = inject(BillingService); // Injected BillingService
  private cdr = inject(ChangeDetectorRef);
  private supabase: SupabaseClient | null = getSupabase();
  private prescriptionSubscription: ReturnType<SupabaseClient['channel']> | null = null;
  private callChannel: ReturnType<SupabaseClient['channel']> | null = null;
  private document: Document = inject(DOCUMENT);

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('phoneInput') phoneInput!: ElementRef<HTMLInputElement>;

  currentUser = this.authService.currentUser;
  currentStep = signal<IntakeStep>('kyc');
  inputMethod = signal<InputMethod>(null);
  
  scannedCustomer = signal<Partial<Customer>>({});
  capturedPhoto = signal<string | null>(null);
  capturedIdCardImage = signal<string | null>(null);
  existingPhotoUrl = signal<string | null>(null);
  existingIdCardUrl = signal<string | null>(null);

  // สถานะถ่ายใหม่
  isRetakingPhoto = signal(false);
  isRetakingIdCard = signal(false);

  selectedSymptoms = signal<string[]>([]);
  otherSymptomText = signal<string>(''); 
  activePrescriptionId = signal<string | null>(null);
  completedPrescription = signal<Prescription | null>(null);
  rejectionReason = signal<string>('');
  consentGiven = signal<boolean>(false); 

  vitals_temperature_c = signal<number | null>(null);
  vitals_pulse_rate = signal<number | null>(null);
  vitals_respiration_rate = signal<number | null>(null);
  vitals_blood_pressure = signal<string>('');
  vitals_weight_kg = signal<number | null>(null);
  vitals_height_cm = signal<number | null>(null);

  isCallModalOpen = signal(false);
  isCallAccepted = signal(false); // สถานะยืนยันรับสายใน UI
  twilioToken = signal<string | null>(null);
  roomName = signal<string | null>(null);
  
  isLoading = signal(false);
  isRestoring = signal(false);
  statusMessage = signal<string>(''); 
  callStatus = signal<string>('');
  
  showCancelConfirm = signal(false);
  ocrError = signal<string | null>(null);
  
  allCustomers = signal<Customer[]>([]);
  customersLoadingError = signal<string|null>(null);
  searchTerm = signal('');
  isDropdownOpen = signal(false);
  uploadTarget = signal<UploadTarget>('ocr');

  isPreviewing = signal(false);
  isGeneratingImage = signal(false);
  prescriptionToPrintId = signal<string | null>(null);
  isPrivacyModalOpen = signal(false);
  initialPolicyTab = signal<'tos' | 'pdpa'>('tos');

  activePartners = signal<StorePartner[]>([]);
  hasInHouseDoctor = signal(false);
  showReferralModal = signal(false);
  selectedReferralTarget = signal<StorePartner | 'INTERNAL' | null>(null);

  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  stream: MediaStream | null = null;

  symptomOptions = [
    'นอนไม่หลับ (Insomnia)',
    'เครียด / วิตกกังวล (Stress/Anxiety)',
    'ปวดกล้ามเนื้อเรื้อรัง (Muscle Pain)',
    'ปวดไมเกรน (Migraine)',
    'เบื่ออาหาร (Loss of Appetite)',
    'ออฟฟิศซินโดรม (Office Syndrome)',
    'อื่นๆ (Other)' 
  ];

  computedAge = computed(() => {
    const dob = this.scannedCustomer().birthDate;
    if (!dob) return null;
    return this.calculateAge(dob);
  });

  isUnderage = computed(() => {
    const age = this.computedAge();
    return age !== null && age < 20;
  });

  filteredCustomers = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) return [];
    return this.allCustomers().filter(c => 
      c.fullName.toLowerCase().includes(term) || 
      (c.phone && c.phone.includes(term)) ||
      (c.hn && c.hn.toLowerCase().includes(term))
    );
  });

  constructor() {
    effect(() => {
      const step = this.currentStep();
      const method = this.inputMethod();
      if (step === 'id-card-photo' || step === 'photo' || (step === 'kyc' && method === 'ocr')) {
        this.startCamera();
      } else {
        this.stopCamera();
      }

      if (step === 'kyc' && method === 'manual') {
          setTimeout(() => this.searchInput?.nativeElement?.focus(), 200);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.callStatus.set(this.translationService.translate('pos.kiosk.waiting.message'));
    try {
        const [hasDoc, partners] = await Promise.all([
            this.dataService.checkStoreHasDoctors(),
            this.dataService.getPartners()
        ]);
        this.hasInHouseDoctor.set(hasDoc);
        this.activePartners.set(partners.filter(p => p.status === 'ACTIVE'));
    } catch (e) {
        console.error("Error initializing hybrid logic:", e);
    }
  }
  
  ngAfterViewInit(): void {
    const step = this.currentStep();
    const method = this.inputMethod();
    if (step === 'id-card-photo' || step === 'photo' || (step === 'kyc' && method === 'ocr')) {
      this.startCamera();
    }
    setTimeout(() => {
        this.selectInputMethod('manual');
    });
  }
  
  ngOnDestroy(): void {
    this.stopCamera();
    this.unsubscribeFromPrescription();
    this.unsubscribeFromCall();
    this.notificationService.stopIncomingAlert();
  }

  private unsubscribeFromPrescription() {
    if (this.prescriptionSubscription) {
      this.supabase?.removeChannel(this.prescriptionSubscription);
      this.prescriptionSubscription = null;
    }
  }

  private unsubscribeFromCall() {
      if (this.callChannel) {
          this.supabase?.removeChannel(this.callChannel);
          this.callChannel = null;
      }
  }

  private setupPrescriptionListener(prescriptionId: string) {
    if (!this.supabase) return;
    this.unsubscribeFromPrescription(); 

    this.prescriptionSubscription = this.supabase
      .channel(`pres-${prescriptionId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'prescriptions', filter: `id=eq.${prescriptionId}` },
        async (payload) => {
          const updatedRecord = payload['new'] as Prescription;
          console.log("[Kiosk] Prescription Updated:", updatedRecord.status);
          
          if (updatedRecord.status === 'APPROVED') {
            const fullPrescription = await this.dataService.getPrescriptionForPrinting(prescriptionId);
            this.completedPrescription.set(fullPrescription);
            this.currentStep.set('completed');
            this.notificationService.playSuccessSound();
            this.unsubscribeFromPrescription();
            this.cdr.detectChanges(); 
          } else if (updatedRecord.status === 'REJECTED') {
            this.rejectionReason.set(updatedRecord.notes || 'No reason provided.');
            this.currentStep.set('rejected');
            this.unsubscribeFromPrescription();
            this.cdr.detectChanges();
          }
        }
      )
      .subscribe();
  }

  private setupCallListener(customerId: string) {
      if (!this.supabase) return;
      this.unsubscribeFromCall();

      const channelName = `customer-call-${customerId}`;
      this.callChannel = this.supabase.channel(channelName)
          .on('broadcast', { event: 'incoming-call' }, (payload) => {
              console.log("[Kiosk] Incoming Call Signal:", payload);
              if (payload['payload']) {
                this.roomName.set(payload['payload'].roomName);
                this.notificationService.playIncomingAlert();
                this.cdr.detectChanges();
              }
          })
          .subscribe();
  }

  private async startCamera() {
    this.stopCamera();
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = this.stream;
        this.videoElement.nativeElement.play();
      }
    } catch (e) {
      this.notificationService.showError('ไม่สามารถเข้าถึงกล้องได้ กรุณาตรวจสอบสิทธิ์การใช้งาน (Camera blocked)');
    }
  }

  stopCamera() {
    if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
    }
  }
  
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
    });
  }

  selectInputMethod(method: InputMethod) { 
    this.inputMethod.set(method);
    if(method === 'manual') this.loadAllCustomers();
  }
  
  resetForm() {
    this.currentStep.set('kyc');
    this.inputMethod.set(null);
    this.scannedCustomer.set({});
    this.capturedIdCardImage.set(null);
    this.capturedPhoto.set(null);
    this.selectedSymptoms.set([]);
    this.otherSymptomText.set('');
    this.consentGiven.set(false);
    this.existingIdCardUrl.set(null);
    this.existingPhotoUrl.set(null);
    this.isRetakingPhoto.set(false);
    this.isRetakingIdCard.set(false);
    this.isCallAccepted.set(false);
    this.unsubscribeFromPrescription(); 
    this.unsubscribeFromCall();
    this.notificationService.stopIncomingAlert();
    this.showReferralModal.set(false);
    this.selectedReferralTarget.set(null);
    this.roomName.set(null);
  }

  goToIdCardPhotoStep() { this.currentStep.set('id-card-photo'); }
  
  triggerFileUpload(target: UploadTarget) { this.uploadTarget.set(target); this.fileInput.nativeElement.click(); }
  
  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const target = this.uploadTarget();
    try {
        const base64 = await this.fileToBase64(file);
        if (target === 'ocr') {
            this.capturedIdCardImage.set(base64);
            this.isLoading.set(true);
            this.statusMessage.set('AI Analyzing ID Card...');
            const customerData = await this.geminiService.extractThaiIdCard(base64.split(',')[1]);
            this.scannedCustomer.update(c => ({...c, ...customerData}));
            this.currentStep.set('kyc-review');
        } else if (target === 'idcard') {
            this.capturedIdCardImage.set(base64);
        }
    } catch (e: any) {
        this.notificationService.handleError(e);
    } finally {
        this.isLoading.set(false);
        this.statusMessage.set('');
        if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = '';
    }
  }

  backToMethodSelection() { this.inputMethod.set(null); this.currentStep.set('kyc'); }
  updateCustomerField(field: keyof Customer, value: any) { this.scannedCustomer.update(c => ({...c, [field]: value})); }
  
  async loadAllCustomers() {
      if(this.allCustomers().length > 0) return;
      try {
          this.allCustomers.set(await this.dataService.getCustomers());
      } catch(e) {
          this.notificationService.handleError(e);
      }
  }

  onSearchInput(value: string) { 
    this.searchTerm.set(value);
    this.scannedCustomer.update(c => ({...c, fullName: value}));
    this.isDropdownOpen.set(true); 
  }
  onSearchFocus() { this.isDropdownOpen.set(true); }
  onSearchBlur() { setTimeout(() => this.isDropdownOpen.set(false), 200); }
  
  selectCustomer(customer: Customer) { 
    this.scannedCustomer.set(customer); 
    this.searchTerm.set(customer.fullName); 
    this.isDropdownOpen.set(false);
    this.existingIdCardUrl.set(customer.idCardUrl || null);
    this.existingPhotoUrl.set(customer.profile_image_url || null);
    this.isRetakingPhoto.set(false);
    this.isRetakingIdCard.set(false);
    setTimeout(() => this.phoneInput?.nativeElement?.focus(), 100);
  }
  
  calculateAge(birthDateString: string): number {
      const birthDate = new Date(birthDateString);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      return age;
  }

  async simulateSmartCardRead() {
    this.isLoading.set(true);
    this.statusMessage.set(this.translationService.translate('pos.kiosk.kyc.statusSimulating'));
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      const mockCardUid = `SIM-${Date.now().toString().slice(-6)}`;
      const mockData: Partial<Customer> = {
        fullName: 'นาย สมชาย ตัวอย่าง (จำลอง)', 
        citizenId: '1234567890123',
        birthDate: '1990-01-15', 
        phone: '0812345678',
        address: '123/45 ถ.สุขุมวิท กรุงเทพมหานคร 10110',
        card_uid: mockCardUid
      };
      this.scannedCustomer.set(mockData);
      this.notificationService.showSuccess('อ่านข้อมูลบัตรประชาชนสำเร็จ');
    } catch (e) {
        this.notificationService.handleError(e);
    } finally {
        this.isLoading.set(false);
        this.statusMessage.set('');
    }
  }

  async captureForOCR() {
    if (!this.videoElement || !this.stream) return;
    const video = this.videoElement.nativeElement;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const base64 = canvas.toDataURL('image/jpeg');
    
    this.capturedIdCardImage.set(base64);
    this.isLoading.set(true);
    this.statusMessage.set('AI Analyzing ID Card...');
    
    try {
        const customerData = await this.geminiService.extractThaiIdCard(base64.split(',')[1]);
        this.scannedCustomer.update(c => ({...c, ...customerData}));
        this.currentStep.set('kyc-review');
        this.notificationService.showSuccess('AI อ่านข้อมูลบัตรประชาชนเรียบร้อยแล้ว');
    } catch (e: any) {
        this.notificationService.handleError(e);
        this.capturedIdCardImage.set(null);
        this.startCamera(); 
    } finally {
        this.isLoading.set(false);
        this.statusMessage.set('');
    }
  }
  
  retakePhoto() { 
    this.capturedPhoto.set(null); 
    this.isRetakingPhoto.set(true); // ปลดล็อคแสดงกล้อง
    this.startCamera(); 
  }

  retakeIdCard() { 
    this.capturedIdCardImage.set(null); 
    this.isRetakingIdCard.set(true); // ปลดล็อคแสดงกล้อง
    this.startCamera(); 
  }

  confirmAndGoToSymptoms() { this.currentStep.set('symptoms'); }
  
  captureImage() {
    if (!this.videoElement || !this.stream) return;
    const canvas = document.createElement('canvas');
    const video = this.videoElement.nativeElement;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.translate(canvas.width, 0); ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    this.capturedPhoto.set(canvas.toDataURL('image/jpeg'));
    this.stopCamera();
    this.notificationService.playSuccessSound();
  }
  
  toggleSymptom(symptom: string) {
    this.selectedSymptoms.update(current => 
        current.includes(symptom) ? current.filter(s => s !== symptom) : [...current, symptom]
    );
  }

  // --- NEW: Pre-flight Financial Check ---
  private async checkReferralCapability(): Promise<boolean> {
      try {
          this.isLoading.set(true);
          const [usage, sub, wallet] = await Promise.all([
              this.billingService.getUsageStats(),
              this.billingService.getSubscription(),
              this.billingService.getWalletBalance()
          ]);

          const limit = sub?.plan?.limits?.medical_cases || 999999;
          const used = usage.medical_cases_used || 0;
          const overageCost = sub?.plan?.limits?.overage_cost || 0;
          
          // Check: Is quota full?
          if (used >= limit) {
              // Check: If full, do we have enough money?
              if (wallet.balance < overageCost) {
                  return false; // Not enough money
              }
          }
          
          return true; // OK to proceed

      } catch (e) {
          console.error("Failed to check referral capability", e);
          // If check fails (e.g. network), we default to blocking for safety, or allow with warning?
          // Let's allow but log error to be user-friendly, or block if strict.
          // Strict approach:
          return false;
      } finally {
          this.isLoading.set(false);
      }
  }

  async initiateSubmission() {
      if (!this.consentGiven()) {
          this.notificationService.showWarning('กรุณกดให้ความยินยอมนโยบายข้อมูลส่วนบุคคล (PDPA)');
          return;
      }
      if (!this.scannedCustomer().fullName || !this.scannedCustomer().phone) {
          this.notificationService.showError('ข้อมูลไม่ครบถ้วน: กรุณาระบุชื่อและเบอร์โทรศัพท์');
          return;
      }
      if (this.selectedSymptoms().length === 0) {
          this.notificationService.showWarning('กรุณาระบุอาการเบื้องต้นของคุณ');
          return;
      }
      
      const store = this.authService.currentStore();
      const partners = this.activePartners();
      const hasDoc = this.hasInHouseDoctor();

      // Case 1: Is Clinic or Has In-house Doctor -> Proceed Internal
      if (store?.businessType === 'CLINIC' || (hasDoc && partners.length === 0)) {
          await this.submitAndEnterWaitingRoom(null); 
          return;
      }

      // Case 2: Is Dispensary (Referral Needed)
      if (store?.businessType === 'DISPENSARY' && !hasDoc) {
          
          // *** PRE-FLIGHT CHECK ***
          const canAfford = await this.checkReferralCapability();
          if (!canAfford) {
               this.notificationService.showError('ไม่สามารถส่งตัวได้: เครดิตใน Wallet ไม่เพียงพอ (Insufficient Funds)');
               return;
          }
          // ************************

          if (partners.length === 0) {
               this.notificationService.showError('ไม่พบแพทย์ออนไลน์ในขณะนี้ กรุณาติดต่อพนักงาน'); return;
          }
          
          // If only 1 partner, auto-select. Else show modal.
          this.selectedReferralTarget.set(partners.length === 1 ? partners[0] : null);
          this.showReferralModal.set(true); 
          return;
      }
      
      // Default fallback
      this.showReferralModal.set(true);
  }

  confirmReferralSelection(target: StorePartner | 'INTERNAL') {
      this.showReferralModal.set(false);
      this.submitAndEnterWaitingRoom(target === 'INTERNAL' ? null : target);
  }

  async submitAndEnterWaitingRoom(referralTarget: StorePartner | null): Promise<void> {
    const customer = this.scannedCustomer();
    const user = this.currentUser();

    // Guard against missing user or storeId
    if (!user || !user.storeId || !user.uid) {
        this.notificationService.showError("User session invalid. Please log in again.");
        return;
    }

    this.isLoading.set(true);
    try {
      let customerId = customer.id;
      if (!customerId) {
        const newCustomer = await this.dataService.addCustomer(customer as Omit<Customer, 'id' | 'createdAt' | 'hn'>);
        customerId = newCustomer.id;
        this.scannedCustomer.update(c => ({ ...c, id: customerId, hn: newCustomer.hn })); 
      } else {
        await this.dataService.updateCustomer(customer as Customer);
      }
      const idCardBase64Raw = this.capturedIdCardImage();
      if (idCardBase64Raw) {
          const processed = await this.customerService.processIdCardImage(idCardBase64Raw, null, customer.fullName!, 'ใช้สำหรับทางการแพทย์เท่านั้น');
          await this.dataService.uploadIdCardPhoto(customerId!, processed);
      }
      const photoBase64 = this.capturedPhoto();
      if (photoBase64) await this.dataService.uploadCustomerPhoto(customerId!, photoBase64);

      await this.dataService.logConsent(customerId!, 'PDPA_MEDICAL_CONSENT', '1.0');
      let finalSymptoms = [...this.selectedSymptoms()];
      if (this.otherSymptomText().trim()) {
          finalSymptoms = finalSymptoms.filter(s => s !== 'อื่นๆ (Other)');
          finalSymptoms.push(this.otherSymptomText().trim());
      }
      const vitals: any = { temperature_c: this.vitals_temperature_c(), pulse_rate: this.vitals_pulse_rate(), blood_pressure: this.vitals_blood_pressure() };
      
      const presc = await this.dataService.createPrescription(
          customerId!, 
          user.storeId!, // Guaranteed string by guard check
          user.uid, 
          finalSymptoms, 
          vitals,
          referralTarget ? user.storeId! : undefined,
          referralTarget ? this.authService.currentStore()?.name : undefined,
          referralTarget?.partner_store_id
      );
      this.activePrescriptionId.set(presc.id);
      
      this.setupPrescriptionListener(presc.id);
      this.setupCallListener(customerId!);

      this.callStatus.set(referralTarget ? `กำลังส่งตัวไปที่ ${referralTarget.partner_name}...` : this.translationService.translate('pos.kiosk.waiting.message'));
      this.currentStep.set('waiting');
      this.notificationService.showSuccess('เข้าคิวตรวจเรียบร้อยแล้ว');
    } catch (e) {
      this.notificationService.handleError(e);
    } finally {
      this.isLoading.set(false);
    }
  }

  // ✅ ปิดเสียงและอัปเดตสถานะรับสาย
  joinIncomingCall() {
      this.notificationService.stopIncomingAlert();
      this.isCallAccepted.set(true);
      this.isCallModalOpen.set(true);
  }

  handleCallEnded() {
      this.isCallModalOpen.set(false);
      // เมื่อวางสาย เรายังคงสถานะ isCallAccepted ไว้จนกว่าจะจบ Session
  }

  requestCancel() { this.showCancelConfirm.set(true); }
  abortCancel() { this.showCancelConfirm.set(false); }
  
  async confirmCancel(): Promise<void> {
      this.showCancelConfirm.set(false); this.isLoading.set(true);
      try {
          if (this.activePrescriptionId()) await this.dataService.cancelPrescription(this.activePrescriptionId()!);
          this.notificationService.stopIncomingAlert();
          this.notificationService.showInfo('ยกเลิกคิวเรียบร้อยแล้ว');
          this.finishSession();
      } catch (e) { this.notificationService.handleError(e); } 
      finally { this.isLoading.set(false); }
  }
  
  finishSession() { this.resetForm(); this.exitPreviewMode(); }
  enterPreviewMode(): void { if (this.completedPrescription()) { this.prescriptionToPrintId.set(this.completedPrescription()!.id); this.isPreviewing.set(true); } }
  exitPreviewMode(): void { this.isPreviewing.set(false); this.prescriptionToPrintId.set(null); }
  
  async printSmartly(): Promise<void> {
    const paper = this.document.querySelector('.A4-paper');
    if (!paper) {
        this.notificationService.showWarning('Document not ready. Please wait.');
        return;
    }
    this.isGeneratingImage.set(true);
    try {
        const styles = Array.from(this.document.querySelectorAll('style, link[rel="stylesheet"]')).map(n => n.outerHTML).join('');
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        this.document.body.appendChild(iframe);
        const doc = iframe.contentWindow?.document;
        doc?.open(); doc?.write(`<html><head>${styles}</head><body>${paper.outerHTML}</body></html>`); doc?.close();
        setTimeout(() => { iframe.contentWindow?.print(); this.document.body.removeChild(iframe); this.isGeneratingImage.set(false); }, 1000);
    } catch (err) { this.notificationService.showError("Failed to print."); this.isGeneratingImage.set(false); }
  }

  async downloadImage() {
    if (typeof html2canvas === 'undefined') {
        this.notificationService.showWarning('Image library missing.');
        return;
    }
    const element = this.document.querySelector('.A4-paper') as HTMLElement;
    if (!element) {
        this.notificationService.showWarning('Document content missing.');
        return;
    }
    this.isGeneratingImage.set(true);
    try {
      const canvas = await html2canvas(element, { scale: 3, useCORS: true, backgroundColor: '#ffffff' });
      const link = this.document.createElement('a');
      link.download = `Medical-Cert.png`; link.href = canvas.toDataURL('image/png'); link.click();
    } catch (err) { this.notificationService.showError("Save failed."); } 
    finally { this.isGeneratingImage.set(false); }
  }

  goToPatientPhotoStep() { this.currentStep.set('photo'); }
  captureIdCardAndProceed() {
    if (!this.videoElement || !this.stream) return;
    const video = this.videoElement.nativeElement;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    this.capturedIdCardImage.set(canvas.toDataURL('image/jpeg'));
    this.stopCamera();
    this.notificationService.playSuccessSound();
  }
  
  openPrivacyModal(tab: 'tos' | 'pdpa'): void { this.initialPolicyTab.set(tab); this.isPrivacyModalOpen.set(true); }
  closePrivacyModal(): void { this.isPrivacyModalOpen.set(false); }
  handlePolicyAcceptance(): void { this.consentGiven.set(true); this.closePrivacyModal(); }
}
